import React from "react";
import { Tooltip } from "@mui/material";
import logo from "../../../new_logo.png";
import Slider from "react-slick";
import classes from "./examples.module.css";
import util from "../../../styles/utilities.module.css";
import ArrowLeft from "../../../images/left-arrow.svg";
import ArrowLeftEmpty from "../../../images/left-arrow-empty.svg";
import ArrowRight from "../../../images/right-arrow.svg";
import ArrowRightEmpty from "../../../images/right-arrow-empty.svg";
import {
  assessmentFormExamples,
  readingComprehensionFormExamples,
} from "../../../utils/assessmentFormExamples";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFormType,
  selectFormData,
  setFormData,
  setFormType,
  setIncludeImages,
  setIncludeParagraphNumbers,
  setReadingPassage,
} from "../../../slices/form";
import { setLanguage } from "../../../slices/auth";
// import removed (useLocation re-imported with useNavigate below)
import { useAppUsageLogger } from "../../../slices/authApi";
import { useLocation, useNavigate } from "react-router-dom";
// Removed Link scroll behavior; we'll navigate directly when selecting examples on toolSelection
import { useFetchContributorBadgesQuery } from "../../../slices/authApi";
import { setDefaultValues as setFormDefaultValues } from "../../../slices/form";

const DEFAULT_BADGE_URL = logo;
const DEFAULT_CONTRIBUTOR_ID = 0;

const Examples = ({
  scrollToRef,
  setExampleSelected,
  exampleSelected,
  userGroup = "",
  showWorksheetTool = null,
}) => {
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logAppUse = useAppUsageLogger();
  const isToolSelection = pathname === "/toolSelection";
  const formType = useSelector(selectFormType);
  const currentFormData = useSelector(selectFormData);

  const { data: badgesData = {} } = useFetchContributorBadgesQuery();

  const badges = badgesData || {};

  function SampleNextArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          top: "10%",
        }}
        onClick={onClick}
      >
        <img
          height={20}
          width={20}
          src={
            className.includes("slick-disabled") ? ArrowRightEmpty : ArrowRight
          }
          alt="arrow_left"
        />
      </div>
    );
  }

  function SamplePrevArrow(props) {
    const { className, style, onClick } = props;

    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          top: "10%",
        }}
        onClick={onClick}
      >
        <img
          height={20}
          width={20}
          src={
            className.includes("slick-disabled") ? ArrowLeftEmpty : ArrowLeft
          }
          alt="arrow_left"
        />
      </div>
    );
  }

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    dotsClass: classes.buttonBar,
  };

  // Shallow + deep merge utility: merges example fields into current, preserving unspecified fields
  const deepMerge = (target, source) => {
    if (Array.isArray(source)) {
      // Prefer example arrays as authoritative
      return source.slice();
    }
    if (source && typeof source === "object") {
      const out = { ...(target && typeof target === "object" ? target : {}) };
      Object.keys(source).forEach((key) => {
        const sVal = source[key];
        const tVal = out[key];
        if (sVal && typeof sVal === "object" && !Array.isArray(sVal)) {
          out[key] = deepMerge(tVal, sVal);
        } else {
          out[key] = sVal;
        }
      });
      return out;
    }
    return source;
  };

  const setSelectedExample = (example) => {
    // Do not clear the entire form; instead, deep-merge example into current state
    dispatch(setFormType({ formType: example.template_type }));
    dispatch(setLanguage(example.language));

    const mergedFormData = deepMerge(currentFormData || {}, example.formData || {});
    dispatch(
      setFormData({
        ...mergedFormData,
        template_type: example.template_type,
        ...(showWorksheetTool !== null && { show_worksheet_tool: showWorksheetTool }),
      })
    );
    // Always set readingPassage from the merged formData for display
    // The text is already in mergedFormData.text for server submission
    dispatch(setReadingPassage(mergedFormData.text || ""));
    dispatch(setIncludeImages(example.includeImages || false));
    dispatch(setIncludeParagraphNumbers(example.includeParagraphNumbers ?? true));
  // Mark example as selected (legacy API kept for backward compatibility; ignore second arg now)
  setExampleSelected && setExampleSelected(true);
  };

  const examples = (() => {
    const lowerUserGroup = userGroup.toLowerCase();

    // Helper function to alternate items from two groups
    const alternateExamples = (group1, group2) => {
      const result = [];
      const maxLength = Math.max(group1.length, group2.length);
      for (let i = 0; i < maxLength; i++) {
        if (i < group1.length) result.push(group1[i]);
        if (i < group2.length) result.push(group2[i]);
      }
      return result;
    };

    // Filter examples based on userGroup
    const filterExamples = (examples) =>
      examples.filter(
        (item) =>
          (item.includeUserGroups.includes("all") ||
            item.includeUserGroups.includes(userGroup)) &&
          !item.excludeUserGroups.includes(userGroup)
      );

    if (isToolSelection) {
      // Separate examples into those with and without contributors
      const assessmentWithContributor = filterExamples(
        assessmentFormExamples.filter((item) => item.contributorId > 0)
      );
      const readingWithContributor = filterExamples(
        readingComprehensionFormExamples.filter((item) => item.contributorId > 0)
      );
      const assessmentWithoutContributor = filterExamples(
        assessmentFormExamples.filter((item) => !item.contributorId)
      );
      const readingWithoutContributor = filterExamples(
        readingComprehensionFormExamples.filter((item) => !item.contributorId)
      );

      // Alternate examples with contributors first, then without contributors
      return [
        ...alternateExamples(assessmentWithContributor, readingWithContributor),
        ...alternateExamples(assessmentWithoutContributor, readingWithoutContributor),
      ];
    } else {
      // Filter examples to include only those with contributorId === 0 if userGroup starts with "bt"
      const filterByContributor = (examples) =>
        lowerUserGroup.startsWith("cy")
          ? examples.filter((item) => !item.contributorId)
          : examples;

      // Return filtered examples based on formType
      return formType === "assessment"
        ? filterByContributor(filterExamples(assessmentFormExamples))
        : filterByContributor(filterExamples(readingComprehensionFormExamples));
    }
  })();


  const onSelectBtnHandler = (item) => {
    // Differentiate logging context based on page
    logAppUse(isToolSelection ? "tool_selection" : "criteria_selection", "example_direct_select");
    setSelectedExample(item);
    if (isToolSelection) {
      localStorage.setItem("from", "toolSelection");
      navigate("/criteriaselection", { state: { showScroll: true, exampleSelected: true } });
    } else {
      // On criteriaSubmission page: smoothly scroll to form area
      try {
        if (scrollToRef?.current) {
          const top = scrollToRef.current.getBoundingClientRect().top + window.pageYOffset - 20;
          window.scrollTo({ top, behavior: "smooth" });
        } else {
          window.scrollTo({ top: 900, behavior: "smooth" });
        }
      } catch (e) {
        // ignore
      }
    }
  };


  return (
    <div
      style={{
        justifyContent: "center",
        alignContent: "center",
        textAlign: "center",
        display: "flex",
        width: "100%",
        maxWidth: "700px",
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: "40px",
        marginBottom: "40px",
        flexDirection: "column",
      }}
    >
      {!isToolSelection ? (
        <p
          style={{ fontSize: "19px", fontWeight: "bold", marginBottom: "30px" }}
        >
          Select an example from the carousel to get started, or fill out the
          form below to create your own
        </p>
      ) : (
        <p
          style={{ fontSize: "19px", fontWeight: "bold", marginBottom: "30px" }}
        >
          Curious for ideas? Browse our carousel for inspiration!
        </p>
      )}
      <div className="slider-container">
        <Slider {...settings}>
          {examples?.map((item) => {
            const contributorId = item.contributorId || DEFAULT_CONTRIBUTOR_ID;
            const badgeUrl = badges[contributorId]?.image_url || DEFAULT_BADGE_URL;
            const exampleTitle =
              item.template_type === "assessment"
                ? "Assessment"
                : "Study a topic";
            return (
              <div key={item.title} className={`${classes.customSlides}`}>
                <p style={{ textAlign: "left" }} className={classes.itemClamp}>
                  <b style={{ color: 'var(--color-primary)' }}>{exampleTitle}: </b>
                  {item.title}
                </p>
                <div className={classes.subjectContainer}>
                  <p className={classes.subjectText}>{item.formData.subject}</p>
                  {contributorId ? (
                    <>
                      <Tooltip
                        arrow
                        title={
                          <span style={{ fontSize: "1rem" }}>
                            {`Contributed by ${
                              badges[contributorId]?.name || "ClassGenius"
                            }`}
                          </span>
                        }
                      >
                        <img
                          src={badgeUrl}
                          alt="Contributor Badge"
                          className={classes.contributorBadge}
                        />
                      </Tooltip>
                    </>
                  ) : (
                    <></>
                  )}
                  <button
                    type="button"
                    onClick={() => onSelectBtnHandler(item)}
                    className={`${util.btn} ${util['btn-sm']}`}
                  >
                    Select
                  </button>
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export default Examples;
